import {AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, ViewChild} from '@angular/core';
import EditorJS from "@editorjs/editorjs";
import Header from "@editorjs/header";
import List from "@editorjs/list";
import Table from "@editorjs/table";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MatButtonToggle, MatButtonToggleGroup} from "@angular/material/button-toggle";
import {MatIcon} from "@angular/material/icon";
import {NgIf} from "@angular/common";

import Paragraph from "@editorjs/paragraph";
import {MatDialog} from "@angular/material/dialog";
import {MediaListComponent} from "../../media-library/media-list/media-list.component";

import ColorPlugin from 'editorjs-text-color-plugin';
import {DialogConfirmComponent} from "../../../shared/components/dialogs/dialog-confirm/dialog-confirm.component";
import {TranslateService} from "@ngx-translate/core";

interface ColorPluginConfig {
  colorCollections: string[];
  defaultColor: string;
  type: 'text' | 'background';
  customPicker: boolean;
}

const colorPluginConfig: ColorPluginConfig = {
  colorCollections: ['#1F273C', '#454DCB', '#999BBB', '#ADA29A'],
  defaultColor: '#1F273C',
  type: 'text',
  customPicker: true
};


@Component({
  selector: 'app-block-style-editor',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonToggle,
    MatButtonToggleGroup,
    MatIcon,
    NgIf,
    ReactiveFormsModule
  ],
  templateUrl: './block-style-editor.component.html',
  styleUrl: './block-style-editor.component.css'
})
export class BlockStyleEditorComponent implements AfterViewInit {
  @Input() contentBlock: any;
  @Input() selectedLanguage: string = 'en';
  @Input() isEditMode: boolean = false;
  @Input() fieldToEdit: string = 'content';

  @Output() languageChange = new EventEmitter<string>();
  @Output() save = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();
  @Output() delete = new EventEmitter<void>();

  @ViewChild('group') group!: MatButtonToggleGroup;
  @ViewChild('editor') editorElement!: ElementRef;
  private editor!: EditorJS;
  public copiedContent!: any;

  constructor(public dialog: MatDialog,
              private _translate: TranslateService) {
  }


  ngAfterViewInit() {
    this.initEditor()
  }


  initEditor() {
    const openMediaLibraryCallback = (callback?: (url: string) => void) => this.openMediaLibrary(callback);

    class SimpleImage {
      data: { url: string };
      openMediaLibrary: (callback?: (url: string) => void) => void;
      inputElement: HTMLInputElement | null = null;
      imageElement: HTMLImageElement | null = null;


      static get toolbox() {
        return {
          title: 'Image',
          icon: '<svg width="17" height="15" viewBox="0 0 336 276" xmlns="http://www.w3.org/2000/svg"><path d="M291 150V79c0-19-15-34-34-34H79c-19 0-34 15-34 34v42l67-44 81 72 56-29 42 30zm0 52l-43-30-56 30-81-67-66 39v23c0 19 15 34 34 34h178c17 0 31-13 34-29zM79 0h178c44 0 79 35 79 79v118c0 44-35 79-79 79H79c-44 0-79-35-79-79V79C0 35 35 0 79 0z"/></svg>'
        };
      }

      constructor({data, config}: {
        data: { url: string },
        config: { openMediaLibrary: (callback?: (url: string) => void) => void }
      }) {
        this.data = data || {url: ''};
        this.openMediaLibrary = openMediaLibraryCallback;
      }

      render() {
        const wrapper = document.createElement('div');
        const input = document.createElement('input');
        const button = document.createElement('button');
        const image = document.createElement('img');

        wrapper.classList.add('simple-image');
        input.value = this.data.url;
        input.style.visibility = 'hidden'; // Hide the input field

        this.inputElement = input;

        // Add event listener to open media library when the image is clicked
        image.addEventListener('click', () => {
          this.openMediaLibrary((selectedUrl: string) => {
            this.setImageUrl(selectedUrl);
          });
        });


        if (!this.data.url) {
          button.textContent = 'Select File';
          button.classList.add('mat-flat-button', 'mat-button-base', 'bp-button');
          button.setAttribute('color', 'primary');
          button.addEventListener('click', () => {
            this.openMediaLibrary((selectedUrl: string) => {
              image.src = 'https://ik.imagekit.io/tlob' + this.data.url;
              image.style.width = '100%';
              image.style.cursor = 'pointer';
              this.imageElement = image;
              wrapper.insertBefore(image, wrapper.firstChild);
              this.setImageUrl(selectedUrl);
              wrapper.removeChild(button);

            });
          });

          wrapper.appendChild(button); // Append button only if the URL is empty
        } else {
          image.src = 'https://ik.imagekit.io/tlob' + this.data.url;
          image.style.width = '100%';
          image.style.cursor = 'pointer';
          this.imageElement = image;
          wrapper.appendChild(image);
        }

        wrapper.appendChild(input);

        return wrapper;
      }


      setImageUrl(url: string) {
        if (this.inputElement) {
          this.inputElement.value = url;
        }
        if (this.imageElement) {
          this.imageElement.src = 'https://ik.imagekit.io/tlob' + url;
        }
      }

      save(blockContent: HTMLElement) {
        const input = blockContent.querySelector('input');
        return {
          url: input ? input.value : ''
        };
      }
    }

    this.editor = new EditorJS({
      holder: this.editorElement.nativeElement,
      autofocus: true,
      tools: {
        paragraph: {
          class: Paragraph,
          inlineToolbar: ['bold', 'italic', 'textColor'],
        },
        header: Header,
        list: {
          class: List as any,
          inlineToolbar: ['bold', 'italic', 'textColor'],
        },
        table: {
          class: Table,
          inlineToolbar: ['bold', 'italic', 'textColor'],
        },
        textColor: {
          class: ColorPlugin,
          config: {
            defaultColor: '#ADA29A',
            type: 'text',
            customPicker: false
          }
        },

        image: SimpleImage,
      },
      data: {
        blocks: this.contentBlock.translations[this.selectedLanguage][this.fieldToEdit]
      }
    });
  }


  openMediaLibrary(callback?: (url: string) => void) {
    const dialogRef = this.dialog.open(MediaListComponent, {
      width: '95vw',
      height: '95vh',
      panelClass: 'media-library-dialog',
      data: {
        // You can pass any data you need to the MediaListComponent
      }
    });

    dialogRef.afterClosed().subscribe((selectedImageUrl: string) => {
      if (selectedImageUrl && callback) {
        callback(selectedImageUrl); // Pass the URL to the callback function
      }
    });
  }


  insertImageInEditor(imageUrl: string) {
    const newBlock = {
      type: 'image',
      data: {
        file: {
          url: imageUrl
        }
      }
    };

    //this.editor.blocks.insert('image', newBlock.data, {}, newBlock.index);
  }


  async changeLanguage(language: string) {
    this.languageChange.emit(language);

    const outputData = await this.editor.save();
    this.contentBlock.translations[this.selectedLanguage][this.fieldToEdit] = outputData.blocks;

    this.editor.isReady.then(async () => {
      if (outputData.blocks && outputData.blocks.length > 0) {
        await this.editor.clear();
      }

      const data = {
        blocks: this.contentBlock.translations[language][this.fieldToEdit]
      };

      this.editor.render(data);
    });
  }


  copyAction() {
    this.editor.save().then(outputData => {
      this.copiedContent = outputData.blocks;
    });
  }

  insertAction() {
    if (this.copiedContent) {
      this.editor.isReady.then(async () => {
        await this.editor.clear(); // Löscht den aktuellen Inhalt im Editor

        const data = {
          blocks: this.copiedContent
        };

        this.editor.render(data); // Fügt den kopierten Inhalt ein
        this.contentBlock.translations[this.selectedLanguage][this.fieldToEdit] = this.copiedContent;
      });
    } else {
      console.warn("No content copied to insert.");
    }
  }

  async saveAction() {
    const outputData = await this.editor.save();
    this.contentBlock.translations[this.selectedLanguage][this.fieldToEdit] = outputData.blocks
    this.save.emit();
  }

  cancelAction() {
    this.cancel.emit();
  }

  resetLanguageSelection() {
    setTimeout(() => {
      this.group.value = this.selectedLanguage;
    });
  }

  confirmDelete() {

    DialogConfirmComponent.openDeleteDialog(this.dialog, this._translate).subscribe(result => {
      if (result) {
        this.deleteAction();
      }
    });
  }

  deleteAction() {
    this.delete.emit();
  }

}
